import React, { useEffect, useRef, useState } from 'react';

/** True when the visitor has asked the OS to reduce motion. */
export const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Fires once when the element first scrolls into view. Used for section
 * reveals and counter animations so nothing animates off-screen (and nothing
 * animates at all for reduce-motion users).
 */
export const useInView = <T extends HTMLElement>(rootMargin = '-80px') => {
  const ref = useRef<T | null>(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSeen(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [seen, rootMargin]);

  return { ref, seen };
};

interface RevealProps {
  children: React.ReactNode;
  className?: string;
  /** Stagger in milliseconds so sibling cards cascade. */
  delay?: number;
  as?: 'div' | 'section' | 'li';
}

/** Fade + rise a block into view the first time it is scrolled to. */
export const Reveal: React.FC<RevealProps> = ({ children, className = '', delay = 0, as = 'div' }) => {
  const { ref, seen } = useInView<HTMLDivElement>();
  const reduced = prefersReducedMotion();
  const Tag = as as keyof JSX.IntrinsicElements;

  return React.createElement(
    Tag,
    {
      ref: ref as React.Ref<never>,
      className: `${className} ${seen || reduced ? 'animate-rise-in' : 'opacity-0'}`,
      style: seen && !reduced ? { animationDelay: `${delay}ms` } : undefined,
    },
    children,
  );
};

interface CountUpProps {
  /** Final value. */
  value: number;
  /** Rendered before/after the number, e.g. "$" / "+". */
  prefix?: string;
  suffix?: string;
  /** Pre-formatted string to show instead of the raw number (e.g. "$184K"). */
  format?: (n: number) => string;
  className?: string;
  durationMs?: number;
}

/**
 * Counts up to a real value when scrolled into view. The final frame is always
 * the exact value passed in, and reduce-motion users see that value instantly —
 * the animation never changes what the number says.
 */
export const CountUp: React.FC<CountUpProps> = ({
  value,
  prefix = '',
  suffix = '',
  format,
  className = '',
  durationMs = 1100,
}) => {
  const { ref, seen } = useInView<HTMLSpanElement>('-40px');
  const reduced = prefersReducedMotion();
  const [n, setN] = useState(reduced ? value : 0);

  useEffect(() => {
    if (!seen) return;
    if (reduced || value <= 0) {
      setN(value);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / durationMs);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setN(value);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [seen, value, durationMs, reduced]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {format ? format(n) : n.toLocaleString('en-US')}
      {suffix}
    </span>
  );
};

export default Reveal;
